/**
 * Structured Data (JSON-LD) Generators
 * Generates Schema.org structured data for SEO
 */

import type { Product, Collection, Cart } from '../shopify/types';

const SITE_URL = import.meta.env.VITE_PUBLIC_STORE_URL || 'https://www.itremarketing.co.uk';
const SITE_NAME = 'IT Remarketing';

// Business Information - Required for Google Merchant Center
const BUSINESS_INFO = {
  name: 'IT Remarketing',
  legalName: 'IT Remarketing Limited',

  telephone: '+44 20 4591 5420',
  email: 'sales@itremarketing.co.uk',
  vatNumber: 'GB477755043',
  companyNumber: '15820855',
  registeredAddress: {
    streetAddress: 'Swan Ken, Broadbridge Heath Road',
    addressLocality: 'Warnham, Horsham',
    addressRegion: 'West Sussex',
    postalCode: 'RH12 3PA',
    addressCountry: 'GB',
  },
  address: {
    streetAddress: '18 Church Street',
    addressLocality: 'Warnham, Horsham',
    addressRegion: 'West Sussex',
    postalCode: 'RH12 3QW',
    addressCountry: 'GB',
  },
};

export interface BreadcrumbItem {
  name: string;
  url: string;
}

// Organization Schema - Enhanced for Google Merchant Center
export function generateOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: BUSINESS_INFO.name,
    legalName: BUSINESS_INFO.legalName,
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: 'https://cdn.shopify.com/s/files/1/0952/3678/4460/files/itr-logo.png?v=1770583066',
      width: 512,
      height: 512,
    },
    description: 'Professional communication headsets from leading brands including Jabra, Poly, and Axtel. Expert advice and next day delivery.',
    telephone: BUSINESS_INFO.telephone,
    email: BUSINESS_INFO.email,
    vatID: BUSINESS_INFO.vatNumber,
    address: {
      '@type': 'PostalAddress',
      streetAddress: BUSINESS_INFO.address.streetAddress,
      addressLocality: BUSINESS_INFO.address.addressLocality,
      addressRegion: BUSINESS_INFO.address.addressRegion,
      postalCode: BUSINESS_INFO.address.postalCode,
      addressCountry: BUSINESS_INFO.address.addressCountry,
    },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: BUSINESS_INFO.telephone,
        contactType: 'customer service',
        availableLanguage: 'English',
        areaServed: 'GB',
      },
      {
        '@type': 'ContactPoint',
        email: BUSINESS_INFO.email,
        contactType: 'sales',
        availableLanguage: 'English',
      },
    ],
    sameAs: [],
  };
}

// Website Schema with SearchAction
export function generateWebsiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
  };
}

// Shared shipping details for all offers
function getShippingDetails(price: number) {
  return {
    '@type': 'OfferShippingDetails',
    shippingRate: {
      '@type': 'MonetaryAmount',
      value: price >= 50 ? '0' : '4.99',
      currency: 'GBP',
    },
    shippingDestination: {
      '@type': 'DefinedRegion',
      addressCountry: 'GB',
    },
    deliveryTime: {
      '@type': 'ShippingDeliveryTime',
      handlingTime: {
        '@type': 'QuantitativeValue',
        minValue: 0,
        maxValue: 1,
        unitCode: 'DAY',
      },
      transitTime: {
        '@type': 'QuantitativeValue',
        minValue: 1,
        maxValue: 2,
        unitCode: 'DAY',
      },
    },
  };
}

// Shared return policy for all offers
const RETURN_POLICY = {
  '@type': 'MerchantReturnPolicy',
  applicableCountry: 'GB',
  returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
  merchantReturnDays: 30,
  returnMethod: 'https://schema.org/ReturnByMail',
  returnFees: 'https://schema.org/FreeReturn',
};

/** Build a product description with a meaningful fallback */
function getProductDescription(product: Product): string {
  return product.description
    || `${product.title} by ${product.vendor}. Professional communication headset available from ${SITE_NAME}.`;
}

// Product Schema - Enhanced for Google Merchant Center
export function generateProductSchema(product: Product) {
  const variant = product.variants.edges.find((e) => e.node.availableForSale)?.node
    ?? product.variants.edges[0]?.node;

  // Extract GTIN from barcode if valid (8-14 digits)
  const barcode = variant?.barcode;
  const hasValidGtin = barcode && /^\d{8,14}$/.test(barcode);

  // Extract MPN from SKU
  const mpn = variant?.sku;

  const itemCondition = (() => {
    const mf = product.metafields?.find((m) => m?.namespace === 'mm-google-shopping' && m?.key === 'condition');
    const condition = mf?.value?.toLowerCase() || '';
    if (condition === 'refurbished') return 'https://schema.org/RefurbishedCondition';
    if (condition === 'used') return 'https://schema.org/UsedCondition';
    if (product.tags?.some((t) => t.toLowerCase() === 'refurbished')) return 'https://schema.org/RefurbishedCondition';
    if (product.title?.toLowerCase().includes('refurbished')) return 'https://schema.org/RefurbishedCondition';
    return 'https://schema.org/NewCondition';
  })();

  // Build individual Offer objects per variant (Google requires these fields on Offer, not AggregateOffer)
  const offers = product.variants.edges.map((edge) => {
    const v = edge.node;
    const vBarcode = v.barcode;
    const vHasGtin = vBarcode && /^\d{8,14}$/.test(vBarcode);
    return {
      '@type': 'Offer',
      url: `${SITE_URL}/products/${product.handle}`,
      price: v.price.amount,
      priceCurrency: v.price.currencyCode,
      availability: v.availableForSale
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      itemCondition,
      ...(v.sku && { sku: v.sku }),
      ...(vHasGtin && { gtin13: vBarcode }),
      seller: {
        '@type': 'Organization',
        name: SITE_NAME,
        url: SITE_URL,
      },
      shippingDetails: getShippingDetails(parseFloat(v.price.amount)),
      hasMerchantReturnPolicy: RETURN_POLICY,
      ...(v.compareAtPrice && parseFloat(v.compareAtPrice.amount) > parseFloat(v.price.amount) && {
        priceValidUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      }),
    };
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': `${SITE_URL}/products/${product.handle}#product`,
    name: product.title,
    description: getProductDescription(product),
    image: product.images.edges.map((edge) => edge.node.url),
    url: `${SITE_URL}/products/${product.handle}`,
    sku: variant?.sku,
    ...(hasValidGtin && { gtin13: barcode }),
    ...(mpn && { mpn }),
    brand: {
      '@type': 'Brand',
      name: product.vendor,
    },
    manufacturer: {
      '@type': 'Organization',
      name: product.vendor,
    },
    category: product.productType || 'Electronics > Audio > Headphones & Headsets',
    itemCondition,
    offers: offers.length === 1 ? offers[0] : {
      '@type': 'AggregateOffer',
      lowPrice: product.priceRange.minVariantPrice.amount,
      highPrice: product.priceRange.maxVariantPrice.amount,
      priceCurrency: product.priceRange.minVariantPrice.currencyCode,
      offerCount: product.variants.edges.length,
      offers,
    },
  };
}

// Product List Schema (for collection pages)
export function generateProductListSchema(products: Product[], collectionTitle: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: collectionTitle,
    numberOfItems: products.length,
    itemListElement: products.map((product, index) => {
      const price = parseFloat(product.priceRange.minVariantPrice.amount);
      return {
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          name: product.title,
          description: getProductDescription(product),
          url: `${SITE_URL}/products/${product.handle}`,
          image: product.featuredImage?.url,
          offers: {
            '@type': 'Offer',
            price: product.priceRange.minVariantPrice.amount,
            priceCurrency: product.priceRange.minVariantPrice.currencyCode,
            availability: product.availableForSale
              ? 'https://schema.org/InStock'
              : 'https://schema.org/OutOfStock',
            shippingDetails: getShippingDetails(price),
            hasMerchantReturnPolicy: RETURN_POLICY,
          },
        },
      };
    }),
  };
}

// Collection/Category Schema
export function generateCollectionSchema(collection: Collection) {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: collection.title,
    description: collection.description,
    url: `${SITE_URL}/collections/${collection.handle}`,
    ...(collection.image && {
      image: collection.image.url,
    }),
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: collection.products.edges.length,
    },
  };
}

// Breadcrumb Schema
export function generateBreadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url.startsWith('http') ? item.url : `${SITE_URL}${item.url}`,
    })),
  };
}

// FAQ Schema (for product pages with FAQs)
export function generateFAQSchema(faqs: Array<{ question: string; answer: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

// Local Business Schema - Enhanced for Google Merchant Center
export function generateLocalBusinessSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Store',
    '@id': `${SITE_URL}/#store`,
    name: BUSINESS_INFO.name,
    legalName: BUSINESS_INFO.legalName,
    url: SITE_URL,
    telephone: BUSINESS_INFO.telephone,
    email: BUSINESS_INFO.email,
    priceRange: '££',
    currenciesAccepted: 'GBP',
    paymentAccepted: 'Credit Card, Debit Card, PayPal, Apple Pay',
    image: 'https://cdn.shopify.com/s/files/1/0952/3678/4460/files/itr-logo.png?v=1770583066',
    address: {
      '@type': 'PostalAddress',
      streetAddress: BUSINESS_INFO.address.streetAddress,
      addressLocality: BUSINESS_INFO.address.addressLocality,
      addressRegion: BUSINESS_INFO.address.addressRegion,
      postalCode: BUSINESS_INFO.address.postalCode,
      addressCountry: BUSINESS_INFO.address.addressCountry,
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 51.0714,
      longitude: -0.3253,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '17:00',
      },
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Professional Headsets',
      itemListElement: [
        {
          '@type': 'OfferCatalog',
          name: 'Jabra Headsets',
          itemListElement: {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Product',
              name: 'Professional Communication Headsets',
            },
          },
        },
      ],
    },
  };
}

// Cart/Order Schema
export function generateOrderSchema(cart: Cart) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Order',
    orderNumber: cart.id,
    orderStatus: 'https://schema.org/OrderPaymentDue',
    acceptedOffer: cart.lines.edges.map((edge) => ({
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Product',
        name: edge.node.merchandise.product.title,
        sku: edge.node.merchandise.id,
      },
      price: edge.node.cost.totalAmount.amount,
      priceCurrency: edge.node.cost.totalAmount.currencyCode,
      eligibleQuantity: {
        '@type': 'QuantitativeValue',
        value: edge.node.quantity,
      },
    })),
  };
}

// Helper to inject JSON-LD into head
export function injectStructuredData(data: object | object[]): string {
  const items = Array.isArray(data) ? data : [data];
  return items
    .map((item) => `<script type="application/ld+json">${JSON.stringify(item, null, 0)}</script>`)
    .join('\n');
}

// Generate all homepage structured data
export function generateHomepageStructuredData() {
  return [generateOrganizationSchema(), generateWebsiteSchema()];
}

// Parse FAQs from product metafield (namespace: custom, key: faqs)
// Expected format: JSON array of { question: string, answer: string }
export function getProductFAQs(product: Product): Array<{ question: string; answer: string }> {
  const mf = product.metafields?.find((m) => m?.namespace === 'custom' && m?.key === 'faqs');
  if (!mf?.value) return [];
  try {
    const parsed = JSON.parse(mf.value);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item: unknown): item is { question: string; answer: string } =>
        typeof item === 'object' && item !== null &&
        typeof (item as any).question === 'string' &&
        typeof (item as any).answer === 'string'
    );
  } catch {
    return [];
  }
}

// Generate all product page structured data
export function generateProductPageStructuredData(
  product: Product,
  breadcrumbs: BreadcrumbItem[]
) {
  const schemas: object[] = [
    generateProductSchema(product),
    generateBreadcrumbSchema(breadcrumbs),
  ];
  const faqs = getProductFAQs(product);
  if (faqs.length) {
    schemas.push(generateFAQSchema(faqs));
  }
  return schemas;
}

// Generate all collection page structured data
export function generateCollectionPageStructuredData(
  collection: Collection,
  products: Product[],
  breadcrumbs: BreadcrumbItem[]
) {
  return [
    generateCollectionSchema(collection),
    generateProductListSchema(products, collection.title),
    generateBreadcrumbSchema(breadcrumbs),
  ];
}
